import { EventType, MediaType } from '../../Chat.enum';
import {
  tAdReferral,
  tAttachment,
  tCalendarEvent,
  tChatEventExplicit,
  tContent,
  tMessage,
} from '../../Chat.types';
import { toChatEventErrors } from '../../chatEventErrors.functions';
import { eAttachmentMediaTypes } from '../../../../providers/SocketsProvider/Sockets.enums';
import {
  tChatEvent as tChatEventSocket,
  tChatEventContentAdReferral,
  tChatEventContentAttachments,
} from '../../../../providers/SocketsProvider/Sockets.types';

/**
 * Maps the wire-level `eAttachmentMediaTypes` enum to the local
 * display-time `MediaType` enum.
 *
 * INT-929 invariant: `SHARE_POST` MUST be matched BEFORE the default branch.
 * Without this, the TikTok link-preview attachment would route to
 * `MediaType.IMAGE` and render a broken bubble (since SHARE_POST has no
 * top-level `url`). Tests in `MessageContainer.functions.test.ts` lock this.
 */
export function mapWireMediaTypeToMediaType(
  wireMediaType: eAttachmentMediaTypes | undefined,
): MediaType {
  switch (wireMediaType) {
    case eAttachmentMediaTypes.IMAGE:
    case eAttachmentMediaTypes.STICKER:
      return MediaType.IMAGE;
    case eAttachmentMediaTypes.VIDEO:
      return MediaType.VIDEO;
    case eAttachmentMediaTypes.AUDIO:
      return MediaType.AUDIO;
    case eAttachmentMediaTypes.DOCUMENT:
      return MediaType.DOCUMENT;
    case eAttachmentMediaTypes.LOCATION:
      return MediaType.LOCATION;
    case eAttachmentMediaTypes.CONTACT:
      return MediaType.CONTACT;
    // INT-929: SHARE_POST MUST be matched BEFORE the default. Without
    // this, SHARE_POST attachments route to IMAGE and render broken.
    case eAttachmentMediaTypes.SHARE_POST:
      return MediaType.SHARE_POST;
    default:
      return MediaType.IMAGE;
  }
}

/**
 * Builds the decrypted `tAttachment` from a wire-shape attachment and the
 * already-resolved `url` (decrypted, if applicable).
 *
 * Propagates the SHARE_POST optional fields (`video_id`, `title`,
 * `author_name`, `author_url`, `thumbnail_url`, `embed_url`) through the
 * translator so downstream renderers can consume them. For non-SHARE_POST
 * attachments these are left undefined.
 */
export function buildDecryptedAttachment(
  attachment: tChatEventContentAttachments,
  resolvedUrl: string | undefined,
): tAttachment {
  return {
    vcard: attachment.vcard || null,
    decryption: {
      encryptedKey: attachment?.decryption?.keys.enc || '',
      iv: attachment?.decryption?.keys.iv || '',
    },
    duration: attachment.duration || null,
    filename: attachment.filename || '',
    latitude: Number(attachment.latitude) || null,
    longitude: Number(attachment.longitude) || null,
    mediaType: mapWireMediaTypeToMediaType(attachment.media_type),
    mimeType: attachment.mimetype || '',
    url: resolvedUrl || '',
    // INT-929 / SHARE_POST: propagate TikTok link-preview fields. Each is
    // the empty string on permanent extraction failure (per U2 contract).
    video_id: attachment.video_id,
    title: attachment.title,
    author_name: attachment.author_name,
    author_url: attachment.author_url,
    thumbnail_url: attachment.thumbnail_url,
    embed_url: attachment.embed_url,
    // INT-1703: copied as-is; only the LOCATION branch renders them as a place.
    name: attachment.name,
    address: attachment.address,
  };
}

/**
 * Maps the wire-level `content.ad_referral` (snake_case container) to the
 * model's `adReferral` (camelCase container) for Meta IG CTIM / Messenger CTM
 * messages. INT-972.
 *
 * The wire (`tChatEventContentAdReferral`) and model (`tAdReferral`) contents
 * are structurally identical — snake_case throughout — so this only remaps the
 * CONTAINER key. The fixed top-level fields are copied explicitly; the loose
 * `ads_context_data` is passed through by reference so unknown Meta sub-keys
 * survive (Meta has added context sub-fields before — INT-933). Returns `null`
 * when absent so the bubble's render floor sees "no card".
 *
 * Extracted as a pure helper so the silent-drop regression — the same class as
 * the documented `link_preview` / `interactive` "renders as plain text until
 * reload" bug — is locked by a unit test without mounting the provider tree. See
 * docs/solutions/logic-errors/websocket-event-payload-regression-defensive-reducer.md.
 */
export function buildAdReferral(
  adReferral: tChatEventContentAdReferral | undefined,
): tAdReferral | null {
  if (!adReferral) {
    return null;
  }

  return {
    source: adReferral.source,
    type: adReferral.type,
    ad_id: adReferral.ad_id,
    ads_context_data: adReferral.ads_context_data,
  };
}

/**
 * INT-1302: builds the `tContent` for a message that arrived over the
 * websocket (`chat_event.inserted`), from the wire event plus the already
 * decrypted attachments.
 *
 * THIS OBJECT IS AN ALLOWLIST, and that is the whole reason it lives here
 * instead of inline in `createNewChatEvent`. A `content` key that is not
 * written down below never reaches the bubble, so the sender sees their own
 * just-sent message render as plain text until a reload rehydrates it from
 * the REST path — the exact bug shape hit by `link_preview`, `interactive`,
 * `ad_referral` and now `carousel`, and documented in
 * docs/solutions/logic-errors/websocket-event-payload-regression-defensive-reducer.md.
 * Extracted as a pure helper so each of those keys can be locked by a unit
 * test without mounting the provider tree, mirroring what AGI-243 did below
 * for the event object itself.
 */
export function buildEventContent(params: {
  event: tChatEventSocket;
  attachments: tAttachment[];
}): tContent {
  const { event, attachments } = params;

  return {
    buttons: event.content.buttons || [],
    attachments: attachments,
    linkPreview: {
      url: event.content.link_preview?.url || '',
      title: event.content.link_preview?.title || '',
      description: event.content.link_preview?.description || '',
      thumbnail:
        event.content.link_preview?.jpegThumbnail?.replace(/^,/, '') || null,
    },
    quotedEventID: event.content?.quoted,
    text: event.content.text || '',
    // Interactive payloads (Reply Buttons / CTA URL / List, plus inbound
    // button_reply / list_reply taps) ride on `content.interactive` through
    // the websocket. Copying it here keeps the socket path at parity with
    // the API fetch path (`ChatService.getChat`) — without this the sender
    // sees their just-sent interactive render as plain text until a page
    // reload rehydrates from the API.
    interactive: event.content.interactive,
    ...(event.content.tiktok ? { tiktok: event.content.tiktok } : {}),
    // Carry Meta's ad-referral context (IG CTIM / Messenger CTM) so a live ad
    // message renders the ad-context card without a reload. The REST /
    // history path is covered separately by bff-web forwarding (INT-972).
    // buildAdReferral remaps the wire's snake_case container `ad_referral` to
    // the model's `adReferral` and returns null when absent.
    adReferral: buildAdReferral(event.content.ad_referral),
    // INT-1302 — carousel cards for a just-sent WhatsApp CAROUSEL template.
    // Passed through with no remap helper (unlike adReferral) because the
    // container key is `carousel` on both the wire and the model, and with no
    // per-key copy because the captured REST and socket payloads for this
    // object are byte-identical.
    carousel: event.content.carousel,
  };
}

/**
 * AGI-243: assembles the final `tChatEvent` model object for a message that
 * arrived over the websocket (`chat_event.inserted`), instead of the inline
 * object literal that used to live in `createNewChatEvent`.
 *
 * Return type is `tChatEventExplicit` (see Chat.types.ts), not `tChatEvent`:
 * a plain `tChatEvent`-typed literal is allowed to silently omit optional
 * fields, which is exactly how `sources` got dropped here — the bubble
 * rendered with no "Ver fuentes" control until the page reloaded and refetched
 * from the REST path instead. `tChatEventExplicit` forces every field,
 * including ones this path intentionally does not populate (`reactions`,
 * `system`, …), to be written down, so a future field silently missing here
 * is a compile error instead of a support ticket. Locked by
 * `MessageContainer.functions.test.ts`.
 */
export function buildChatEventFromSocketEvent(params: {
  event: tChatEventSocket;
  chatID: string;
  content: tContent;
  message: tMessage;
  type: EventType;
  agendaEvent: tCalendarEvent | null;
}): tChatEventExplicit {
  const { event, chatID, content, message, type, agendaEvent } = params;

  return {
    agendaEvent,
    chatID,
    content,
    createdAt: event.created_at,
    createdBy: event.created_by,
    deletedAt: '',
    followUp: null,
    id: event.id,
    message,
    note: null,
    type,
    updatedAt: event.updated_at,
    providerId: event._provider_id || event.provider_id,
    // AGI-243: the Lead Agent grounding sources — the field that three
    // earlier hand-rolled reconstructions dropped. Carried verbatim from
    // the wire; MessageSources itself renders nothing when absent.
    sources: event.sources,
    // Not carried by the socket path today — explicit `undefined` is the
    // decision, not an omission.
    system: undefined,
    reactions: undefined,
    index: undefined,
    selectedIndex: undefined,
    isWapiTemplate: undefined,
    state: undefined,
    // INT-1500 U3 / C3: an event that arrives already failed keeps its error
    // rows, Meta's code and details included.
    errors: toChatEventErrors(event.errors),
  };
}

/**
 * BUG-783: `parseAndInsertMessage` is async and awaits `createNewChatEvent`
 * before its own dedup guard runs. If a second invocation for the same event
 * id starts while the first is still awaiting, both can pass the pre-await
 * checks and both apply side effects (this is the "guard reads mutable state
 * after an await" pattern in
 * docs/solutions/logic-errors/websocket-race-overwrites-guard-state-during-await.md).
 *
 * `tryStart` must be called synchronously, before the first `await`, so the
 * second overlapping call is rejected before doing any async work. `finish`
 * must run in a `finally` so a rejected/failed call doesn't permanently block
 * a legitimate later delivery for the same id.
 */
export function createInFlightGuard() {
  const idsInFlight = new Set<string>();

  return {
    tryStart(id: string): boolean {
      if (idsInFlight.has(id)) {
        return false;
      }
      idsInFlight.add(id);
      return true;
    },
    finish(id: string): void {
      idsInFlight.delete(id);
    },
  };
}
